import { z } from 'zod';
import { SETTINGS_ROLES } from './settings.js';
/**
 * Team settings (ehr#60 — the CRM "Users & Profile" modal): custom user roles,
 * the practice's user-title list, and each user's working hours.
 *
 * Access in the EHR is still decided by the five built-in roles. A custom role is
 * a named role that grants the access of one built-in role (`baseRole`); a user
 * on a custom role carries both `customRoleId` and that base `role`.
 */
export const customRoleSchema = z.object({
    id: z.string().min(1).max(40),
    name: z.string().trim().min(1).max(60),
    baseRole: z.enum(SETTINGS_ROLES),
});
/**
 * ehr#709 - a custom role still held by active users can't be deleted. The API's 409
 * ROLE_IN_USE and the User Roles screen both say it this way.
 */
export const roleInUseMessage = (name, n) => `“${name}” still has ${n} active user${n === 1 ? '' : 's'}. Move ${n === 1 ? 'them' : 'everyone on it'} to another role in Users & Profile, then delete it.`;
export const teamRolesSchema = z.object({
    roles: z
        .array(customRoleSchema)
        .max(50)
        .refine((rs) => new Set(rs.map((r) => r.id)).size === rs.length, 'Duplicate role id')
        .refine((rs) => new Set(rs.map((r) => r.name.toLowerCase())).size === rs.length, 'Two roles have the same name'),
});
export const userTitleSchema = z.object({
    id: z.string().min(1).max(40),
    label: z.string().trim().min(1).max(80),
});
export const teamTitlesSchema = z.object({
    titles: z
        .array(userTitleSchema)
        .max(100)
        .refine((ts) => new Set(ts.map((t) => t.label.toLowerCase())).size === ts.length, 'Two titles have the same name'),
});
/** The practice's starting title list, used until an admin edits it. */
export const DEFAULT_USER_TITLES = [
    'Practice Owner',
    'Clinical Director',
    'Practice Manager',
    'Clinical Supervisor',
    'Licensed Clinician',
    'Associate Clinician',
    'Psychiatrist',
    'Psychiatric Nurse Practitioner',
    'Intake Coordinator',
    'Front Desk',
    'Billing Specialist',
    'Administrator',
];
// ── Hours ────────────────────────────────────────────────────────────────────
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM');
const optHhmm = z.union([hhmm, z.literal('')]);
export const timeRangeSchema = z.object({ from: hhmm, to: hhmm });
/** A break or meal slot; both ends empty means "none". */
export const optRangeSchema = z.object({ from: optHhmm, to: optHhmm });
export const hoursDaySchema = z.object({
    on: z.boolean(),
    shifts: z.array(timeRangeSchema).min(1).max(2),
    breaks: z.array(optRangeSchema).max(2).default([]),
    meal: optRangeSchema.nullable().default(null),
});
/** Days keyed '0' (Sunday) … '6' (Saturday), as in the CRM. */
const DAY_KEYS = ['0', '1', '2', '3', '4', '5', '6'];
export const hoursWeekSchema = z.object(Object.fromEntries(DAY_KEYS.map((d) => [d, hoursDaySchema])));
export const HOURS_MODES = ['operational', 'afterHours', 'splitShift'];
// Time Off / Pay / Timesheet tabs of the hours window (EHR_08-14-V38 renderUserHoursModal).
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const optHhmm24 = z.union([z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/), z.literal('')]);
export const TIME_OFF_TYPES = ['Vacation', 'Sick', 'Personal', 'Unpaid'];
export const TIME_OFF_STATUSES = ['Pending', 'Approved', 'Denied'];
export const timeOffSchema = z
    .object({
    start: isoDate,
    end: isoDate,
    type: z.enum(TIME_OFF_TYPES),
    note: z.string().trim().max(300).default(''),
    status: z.enum(TIME_OFF_STATUSES).default('Pending'),
})
    .refine((t) => t.end >= t.start, 'End date must be on or after the start date');
/** A money / hours input: a non-negative number, or '' while blank. */
const amount = z.union([z.number().min(0).max(1_000_000), z.literal('')]).default('');
export const PAY_FREQUENCIES = ['daily', 'weekly', 'biweekly', 'monthly'];
export const paySchema = z.object({
    basis: z.enum(['hourly', 'daily']).default('hourly'),
    hourly: amount,
    daily: amount,
    frequency: z.enum(PAY_FREQUENCIES).default('biweekly'),
    otHours: amount,
    otMult: z.union([z.number().min(1).max(10), z.literal('')]).default(1.5),
    bonuses: z
        .array(z.object({ min: amount, max: amount, pct: amount }))
        .max(20)
        .default([]),
});
export const timesheetEntrySchema = z.object({
    date: isoDate,
    start: optHhmm24.default(''),
    end: optHhmm24.default(''),
    hours: z.number().min(0).max(24),
    note: z.string().trim().max(300).default(''),
    source: z.enum(['clock', 'manual']),
});
export const timesheetSchema = z.object({
    /** Set while clocked in: when the shift started (ISO timestamp). */
    open: z.object({ start: z.string().datetime() }).nullable().default(null),
    entries: z.array(timesheetEntrySchema).max(5000).default([]),
});
export const userHoursSchema = z
    .object({
    modes: z.object({
        operational: hoursWeekSchema,
        afterHours: hoursWeekSchema,
        splitShift: hoursWeekSchema,
    }),
    effectiveDate: z.union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/), z.literal('')]).default(''),
    timeOff: z.array(timeOffSchema).max(500).default([]),
    pay: paySchema.optional(),
    timesheet: timesheetSchema.optional(),
})
    .superRefine((h, ctx) => {
    for (const mode of HOURS_MODES) {
        for (const d of DAY_KEYS) {
            const day = h.modes[mode][d];
            if (!day.on)
                continue;
            const ranges = [...day.shifts, ...day.breaks, ...(day.meal ? [day.meal] : [])];
            for (const r of ranges) {
                if (!r.from && !r.to)
                    continue;
                if (!r.from || !r.to || r.to <= r.from) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['modes', mode, d],
                        message: 'Each time range must end after it starts',
                    });
                    return;
                }
            }
        }
    }
});
export const userHoursPutSchema = z.object({ hours: userHoursSchema.nullable() });
/**
 * ehr#320 - Users & Profile → Supervisor: who signs off this user's notes and forms (the
 * Supervision setting of Provider Profile → Forms and Supervision). null = no supervisor.
 */
export const userSupervisorPutSchema = z.object({
    supervisorId: z.string().trim().min(1).max(64).nullable(),
});
// ── Users ────────────────────────────────────────────────────────────────────
/**
 * ehr#232 - "Will this person see clients?" Providers (Yes) fill every provider picker
 * across the fleet; support staff (No) don't. When a new user's answer isn't given, the
 * role decides: clinicians and supervisors see clients.
 */
export const SEES_CLIENTS_QUESTION = 'Will this person see clients?';
export const SEES_CLIENTS_YES = 'Yes, they see clients';
export const SEES_CLIENTS_NO = 'No, they support the team (front desk, call center, billing, admin)';
export function defaultSeesClients(role) {
    return role === 'clinician' || role === 'supervisor';
}
export const userCreateSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(200),
    firstName: z.string().trim().min(1).max(60),
    lastName: z.string().trim().min(1).max(60),
    role: z.enum(SETTINGS_ROLES),
    customRoleId: z.string().min(1).max(40).nullable().optional(),
    title: z.string().trim().max(80).nullable().optional(),
    contactInfo: z.lazy(() => contactInfoSchema).optional(),
    /** ehr#232 - omitted: defaultSeesClients(role). */
    seesClients: z.boolean().optional(),
});
/** The mockup's avatar palette (USER_COLOR_PALETTE); a click on the swatch cycles it. */
export const USER_COLORS = [
    '#1a8a66',
    '#7c3aed',
    '#0e7490',
    '#b45309',
    '#c0392b',
    '#3b72d9',
    '#db2777',
    '#4d7c0f',
];
/** Profile & contact info from the V38 user profile window (phone, time zone, location, home address). */
export const contactInfoSchema = z.object({
    phone: z.string().trim().max(30).default(''),
    /** 'device' (the viewer's own zone) or an IANA name such as America/Los_Angeles. */
    timezone: z.string().trim().max(60).default('device'),
    locationId: z.string().trim().max(60).default(''),
    line1: z.string().trim().max(120).default(''),
    line2: z.string().trim().max(120).default(''),
    city: z.string().trim().max(80).default(''),
    state: z.string().trim().max(2).default(''),
    zip: z.string().trim().max(10).default(''),
});
export const userUpdateSchema = z
    .object({
    firstName: z.string().trim().min(1).max(60),
    lastName: z.string().trim().min(1).max(60),
    role: z.enum(SETTINGS_ROLES),
    customRoleId: z.string().min(1).max(40).nullable(),
    title: z.string().trim().max(80).nullable(),
    color: z.enum(USER_COLORS).nullable(),
    contactInfo: contactInfoSchema,
    seesClients: z.boolean(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
// ── Rights (V38 PERMISSION_CATALOG) ─────────────────────────────────────────
/**
 * Feature rights from the mockup's Access Rights / Role Rights windows. A role holds a
 * map of granted keys; a user either rides their role ('role') or holds overrides on top
 * of it ('custom'). Admins always have every right. These are stored and served as
 * effective rights (GET /me/rights); the EHR's own route guards still use the five
 * built-in roles until each screen is wired to a right.
 */
export const RIGHTS_CATALOG = [
    {
        group: 'Records',
        perms: [
            { key: 'prospect_create', label: 'Create records' },
            { key: 'prospect_edit', label: 'Edit record fields' },
            { key: 'delete_Contact', label: 'Delete contacts' },
            { key: 'prospect_move_stage', label: 'Move records between stages' },
            {
                key: 'status_view',
                label: 'View contact Status (badge under the name, Status column, and Statuses/History in the right-click contact menu)',
            },
            { key: 'status_advance', label: 'Advance or regress contact Status (forward & backward)' },
            { key: 'stage_advance', label: 'Advance or regress contact Stage (forward & backward)' },
            { key: 'prospect_merge', label: 'Merge duplicates' },
            { key: 'lock_contact', label: 'Lock contact from redistribution & reassign' },
            {
                key: 'prospect_reassign',
                label: 'Reassign records (via the Delete/Reassign/Archive prompt)',
            },
            {
                key: 'prospect_archive',
                label: 'Archive records (via the Delete/Reassign/Archive prompt)',
            },
            { key: 'bulk_actions', label: 'Bulk actions (multi-select)' },
            {
                key: 'contact_engagement_status',
                label: 'Set contact engagement status (Active / Inactive / Archived)',
            },
            { key: 'change_contact_category', label: 'Change contact category / pipeline type' },
            {
                key: 'view_others_contacts',
                label: 'View others’ contacts (enables the “Show Contacts” filter to browse contacts you own, are granted, or have been shared with; not a blanket org-wide view)',
            },
            {
                key: 'view_multi_user_contacts',
                label: 'Multi-user contact view — check multiple users simultaneously to see contacts shared between all of them (strict intersection)',
            },
            {
                key: 'view_referral_bucket',
                label: 'See the Referrals Bucket (unassigned referrals waiting to be pulled)',
            },
            {
                key: 'archive_contact',
                label: 'Archive a contact (soft-archive — hides from lists, recoverable by admin)',
            },
            {
                key: 'status_transition',
                label: 'Change client status via the Contact Actions bar (Activate, Discharge, Reactivate)',
            },
        ],
    },
    {
        group: 'Activity & follow-up',
        perms: [
            { key: 'note_log', label: 'Add notes' },
            { key: 'activity_log', label: 'Log activity / status' },
            { key: 'followup_set', label: 'Set & clear follow-ups' },
            { key: 'task_manage', label: 'Create & manage tasks' },
            { key: 'task_delete', label: 'Delete tasks' },
            { key: 'touch_rule_enroll', label: 'Auto-enroll new contacts in Touch Rules' },
            {
                key: 'contact_share_reassign',
                label: 'Share or reassign contacts (existing contacts only — does not gate the New Contact share toggle)',
            },
            {
                key: 'view_stage_actions',
                label: 'View Stage Actions tab and Stage Actions in the right-click contact menu',
            },
        ],
    },
    {
        group: 'Sharing (notes, tasks, files)',
        perms: [
            { key: 'share_notes_view', label: 'Share notes with other users (view-only)' },
            { key: 'share_notes_edit', label: 'Share notes with other users (edit rights)' },
            { key: 'share_tasks_view', label: 'Share tasks with other users (view-only)' },
            { key: 'share_tasks_edit', label: 'Share tasks with other users (edit rights)' },
            { key: 'share_files_view', label: 'Share files / documents with other users (view-only)' },
            { key: 'share_files_edit', label: 'Share files / documents with other users (edit rights)' },
        ],
    },
    {
        group: 'Communication',
        perms: [{ key: 'message_send', label: 'Send messages, email & team chat' }],
    },
    {
        group: 'Data',
        perms: [
            { key: 'view_manage', label: 'Create & edit saved views' },
            { key: 'export_data', label: 'Export data (CSV / JSON)' },
            { key: 'import_data', label: 'Import / reset all data' },
            {
                key: 'search_all_contacts',
                label: 'Search all contacts across all lists (otherwise scoped to owned records)',
            },
            { key: 'columns_manage', label: 'Show / hide columns in the contact list' },
            {
                key: 'share_contact_at_creation',
                label: 'Share a new contact at creation time (the "Add for myself & share" toggle)',
            },
        ],
    },
    {
        group: 'Organization (admin)',
        perms: [
            { key: 'manage_stages', label: 'Edit stages' },
            { key: 'manage_schema', label: 'Edit custom fields' },
            { key: 'manage_statuses', label: 'Edit status types' },
            { key: 'manage_routing', label: 'Edit routing rules' },
            { key: 'manage_quotas', label: 'Edit quotas' },
            { key: 'manage_users', label: 'Manage users & roles' },
            { key: 'view_audit_log', label: 'View the audit log' },
            { key: 'commissions_manage', label: 'Manage commissions & payouts' },
            { key: 'automations_manage', label: 'Create & manage automations' },
            {
                key: 'edit_dashboard_view',
                label: 'Edit their own Dashboard View (which categories they see)',
            },
        ],
    },
    {
        group: 'PHI (Client records)', // ehr#257: client, not patient
        perms: [
            { key: 'view_phi', label: 'View PHI fields on client records' },
            { key: 'edit_phi', label: 'Edit PHI fields on client records' },
            { key: 'export_phi', label: 'Include PHI in exports (otherwise redacted)' },
        ],
    },
    {
        // ehr#703 (Jay 10-30, Q2): "make this user settings. By default, keep it open to all"
        group: 'Practice-wide lists',
        perms: [
            {
                key: 'touch_queue_all_clients',
                label: 'See every client in the Touch queue (off: only the clients they own or that are shared with them)',
            },
            {
                key: 'balances_all_clients',
                label: 'See every client in Outstanding balances (off: only the clients they own or that are shared with them)',
            },
        ],
    },
];
export const RIGHTS_KEYS = RIGHTS_CATALOG.flatMap((g) => g.perms.map((p) => p.key));
/**
 * ehr#703 (Jay 10-30, Q2): rights that start on for everyone. A practice may have saved its role
 * rights before these existed (a saved map holds only the granted keys), so a role map without
 * one of these keys grants it; only an explicit false (an admin turned it off) takes it away.
 * PUT role-rights keeps that false (see roleGrants).
 */
export const DEFAULT_ON_RIGHTS = [
    'touch_queue_all_clients',
    'balances_all_clients',
];
const CLINICAL_RIGHTS = [
    'prospect_create',
    'prospect_edit',
    'status_view',
    'status_advance',
    'stage_advance',
    'note_log',
    'activity_log',
    'followup_set',
    'task_manage',
    'task_delete',
    'touch_rule_enroll',
    'message_send',
    'share_notes_view',
    'share_notes_edit',
    'share_tasks_view',
    'share_tasks_edit',
    'share_files_view',
    'share_files_edit',
    'view_manage',
    'columns_manage',
    'status_transition',
    'view_phi',
    'edit_phi',
    ...DEFAULT_ON_RIGHTS,
];
/** Starting rights for the built-in roles until an admin edits them (admin = everything). */
export const DEFAULT_ROLE_RIGHTS = {
    admin: RIGHTS_KEYS,
    manager: RIGHTS_KEYS.filter((k) => k !== 'export_phi' && k !== 'import_data'),
    clinician: CLINICAL_RIGHTS,
    supervisor: [...CLINICAL_RIGHTS, 'view_others_contacts', 'view_audit_log', 'bulk_actions'],
    front_desk: [
        'prospect_create',
        'prospect_edit',
        'status_view',
        'status_advance',
        'change_contact_category',
        'view_others_contacts',
        'view_referral_bucket',
        'archive_contact',
        'note_log',
        'activity_log',
        'followup_set',
        'task_manage',
        'touch_rule_enroll',
        'message_send',
        'share_contact_at_creation',
        'search_all_contacts',
        'columns_manage',
        ...DEFAULT_ON_RIGHTS,
    ],
};
const rightsMap = z
    .record(z.string(), z.boolean())
    .refine((m) => Object.keys(m).every((k) => RIGHTS_KEYS.includes(k)), 'Unknown right');
/** A role's rights: role key 'sys:<builtin>' or 'cr:<custom role id>'. */
export const roleRightsPutSchema = z.object({ rights: rightsMap });
export const userRightsPutSchema = z.object({
    mode: z.enum(['role', 'custom']),
    overrides: rightsMap.default({}),
});
/** Role key → granted map, falling back to the built-in defaults. */
export function roleRightsFor(roleKey, baseRole, stored) {
    const saved = stored?.[roleKey];
    if (saved)
        return saved;
    return Object.fromEntries((DEFAULT_ROLE_RIGHTS[baseRole] ?? []).map((k) => [k, true]));
}
/** ehr#703: whether a role map grants a right (DEFAULT_ON_RIGHTS: granted unless saved false). */
export function roleGrants(roleMap, key) {
    if (roleMap[key] === true)
        return true;
    return DEFAULT_ON_RIGHTS.includes(key) && roleMap[key] !== false;
}
/** ehr#703: a role map with each DEFAULT_ON_RIGHTS key spelled out (the Role Rights window). */
export function withDefaultOnRights(roleMap) {
    const out = { ...roleMap };
    for (const k of DEFAULT_ON_RIGHTS)
        out[k] = roleGrants(roleMap, k);
    return out;
}
/**
 * The role map as it is saved: the granted keys, plus an explicit false for a DEFAULT_ON_RIGHTS
 * key turned off (left out, it would read as granted again).
 */
export function savedRoleRights(rights) {
    return Object.fromEntries(Object.entries(rights).filter(([k, v]) => v === true || (v === false && DEFAULT_ON_RIGHTS.includes(k))));
}
/** Effective rights: admins get all; custom mode layers overrides on the role's map. */
export function effectiveRights(baseRole, roleMap, user) {
    const out = {};
    for (const k of RIGHTS_KEYS) {
        const fromRole = baseRole === 'admin' || roleGrants(roleMap, k);
        const ov = user?.mode === 'custom' ? user.overrides[k] : undefined;
        out[k] = baseRole === 'admin' ? true : typeof ov === 'boolean' ? ov : fromRole;
    }
    return out;
}
// ── User groups ──────────────────────────────────────────────────────────────
export const userGroupSchema = z.object({
    id: z.string().min(1).max(40),
    name: z.string().trim().min(1).max(60),
    memberIds: z.array(z.string().min(1).max(60)).max(1000).default([]),
});
export const teamGroupsSchema = z.object({
    groups: z
        .array(userGroupSchema)
        .max(100)
        .refine((gs) => new Set(gs.map((g) => g.name.toLowerCase())).size === gs.length, 'Two groups have the same name'),
});
/** The mockup's starting groups (names only, no members). */
export const DEFAULT_USER_GROUPS = [
    'Intake Team',
    'Clinical Staff',
    'Call Center',
    'Billing & RCM',
    'On-Call Counselors',
    'Day Shift Leads',
    'Crisis Response Team',
];
// ── Dashboard View (which contact category tabs show, their order and labels) ──
export const DASHBOARD_CATEGORIES = ['client', 'referral_source', 'staff'];
export const DASHBOARD_BUTTONS = ['referral_bucket', 'export', 'notifications'];
export const dashboardViewSchema = z
    .object({
    order: z.array(z.enum(DASHBOARD_CATEGORIES)).max(DASHBOARD_CATEGORIES.length),
    visible: z.record(z.enum(DASHBOARD_CATEGORIES), z.boolean()).default({}),
    labels: z.record(z.enum(DASHBOARD_CATEGORIES), z.string().trim().max(40)).default({}),
    buttons: z.record(z.enum(DASHBOARD_BUTTONS), z.boolean()).default({}),
})
    .refine((v) => new Set(v.order).size === v.order.length, 'Duplicate category')
    .refine((v) => DASHBOARD_CATEGORIES.some((c) => v.visible[c] !== false), 'At least one category must remain visible');
export const DEFAULT_DASHBOARD_VIEW = {
    order: [...DASHBOARD_CATEGORIES],
    visible: {},
    labels: {},
    buttons: {},
};
export const dashboardViewPutSchema = z.object({
    view: dashboardViewSchema,
    /** Also push this view to these users, and to everyone holding these role keys. */
    pushUserIds: z.array(z.string().min(1).max(60)).max(1000).default([]),
    pushRoles: z.array(z.string().min(1).max(60)).max(100).default([]),
});
/** A user's own Dashboard View; null clears it back to the practice default. */
export const userDashboardViewPutSchema = z.object({ view: dashboardViewSchema.nullable() });
// ── Form Mapping / User Rights Mapping (placeholders until the modules are wired) ──
export const formMappingSchema = z.object({
    id: z.string().min(1).max(40),
    scope: z.enum(['role', 'title', 'user']),
    target: z.string().trim().min(1).max(80),
    form: z.string().trim().min(1).max(300),
});
export const teamMappingsSchema = z.object({
    formMappings: z.array(formMappingSchema).max(500).default([]),
    rightsMapping: z.string().trim().max(300).default(''),
});
// ── System (service) accounts ────────────────────────────────────────────────
/**
 * Service identities other modules sign in as, e.g. the client portal's
 * `portal-svc+<orgId>@credifyfast.com` (manager). They must stay active and keep their
 * role, so Users & Profile hides them and the settings API refuses to change them.
 */
export const SERVICE_USER_EMAIL = /^[a-z0-9._-]+-svc\+[^@]+@/i;
export const isServiceUserEmail = (email) => SERVICE_USER_EMAIL.test(email ?? '');
//# sourceMappingURL=team.js.map